import { useMemo } from "react"
import {
	Bar,
	BarChart,
	CartesianGrid,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import {
	reportingBaselineClassName,
	ReportingChartFrame,
	reportingChartTheme,
	reportingGridClassName,
	toneColor,
} from "@/components/reporting/charts"
import { useReportingBarHoverCursor } from "@/components/reporting/charts/use-dark-mode"
import { api } from "@/lib/api"
import { formatWeekLabel, formatWeekRangeLabel } from "./week-label"

interface TooltipPayload {
	active?: boolean
	payload?: Array<{
		payload: {
			date: string
			rangeLabel: string
			workspaces: number
			activated: number
			rate: number
		}
	}>
}

const ActivationTooltip = ({ active, payload }: TooltipPayload) => {
	if (!active || !payload?.length) return null
	const point = payload[0]?.payload
	if (!point) return null
	return (
		<div className="rounded-lg bg-white px-3 py-2 text-xs ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<p className="mb-1.5 font-medium text-primary">{point.rangeLabel}</p>
			<p className="text-secondary">
				Reached a resolution: {point.activated} of {point.workspaces}
			</p>
			<p className="font-medium text-primary">Activation: {point.rate}%</p>
		</div>
	)
}

export function GrowthActivationChart() {
	const query = api.analytics.growthActivationChart.useQuery({})
	const data = query.data
	const hoverCursor = useReportingBarHoverCursor()

	// Partial weeks are dropped rather than plotted: their workspaces have not
	// had the full window yet, so they read as a collapse that later reverses.
	const chartData = useMemo(
		() =>
			(data?.chartData ?? [])
				.filter((point) => !point.isPartial)
				.map((point) => ({
					date: formatWeekLabel(point.bucket),
					rangeLabel: formatWeekRangeLabel(point.bucket),
					workspaces: point.workspaces,
					activated: point.activated,
					rate: point.rate,
				})),
		[data],
	)

	return (
		<ReportingChartFrame
			title="Activation: new workspaces reaching a first resolution"
			description={`Workspaces created per week, and how many earned their first agent resolution within ${data?.windowDays ?? 7} days of signing up. Complete weeks only, since a fresh week has not had the full window.`}
			isLoading={query.isLoading}
			isEmpty={!query.isLoading && chartData.length === 0}
			legend={
				data ? (
					<div className="text-right">
						<p className="text-2xl font-semibold tabular-nums text-primary">
							{data.totals.rate}%
						</p>
						<p className="text-xs text-tertiary">
							{data.totals.activated} of {data.totals.workspaces} new workspaces
						</p>
					</div>
				) : undefined
			}
		>
			<div className="h-[220px] w-full [&_*]:outline-none">
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={chartData}
						margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
					>
						<CartesianGrid
							className={reportingGridClassName}
							vertical={false}
						/>
						<ReferenceLine y={0} className={reportingBaselineClassName} />
						<XAxis
							dataKey="date"
							tickLine={false}
							axisLine={false}
							interval="preserveStartEnd"
							tick={{ fontSize: 11 }}
							className="text-xs text-gray-500 dark:text-gray-400"
						/>
						<YAxis
							orientation="right"
							tickLine={false}
							axisLine={false}
							width={32}
							className="text-xs text-gray-500 dark:text-gray-400"
						/>
						<Tooltip cursor={hoverCursor} content={<ActivationTooltip />} />
						<Bar
							dataKey="workspaces"
							name="Workspaces created"
							fill={reportingChartTheme.muted}
							maxBarSize={reportingChartTheme.maxBarSize}
							radius={[
								reportingChartTheme.barRadius,
								reportingChartTheme.barRadius,
								0,
								0,
							]}
							isAnimationActive={false}
						/>
						<Bar
							dataKey="activated"
							name="Reached a resolution"
							fill={toneColor("primary")}
							maxBarSize={reportingChartTheme.maxBarSize}
							radius={[
								reportingChartTheme.barRadius,
								reportingChartTheme.barRadius,
								0,
								0,
							]}
							isAnimationActive={false}
						/>
					</BarChart>
				</ResponsiveContainer>
			</div>
		</ReportingChartFrame>
	)
}
